import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

import { PageHeader } from '@ems/ui/patterns/page-header'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { ServiceOrdersSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import { catalogQuery, serviceCategoriesQuery } from '#admin/features/exhibitor/queries.ts'
import { ServiceCatalogTable } from '#admin/features/exhibitor/service-catalog-table.tsx'
import type { ServiceFilters } from '#admin/features/exhibitor/service-filters.tsx'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

const text = (value: unknown, max = 200) => (typeof value === 'string' ? value.slice(0, max) : '')

/**
 * `/exhibition/service-items` — the services exhibitors order, with their prices.
 *
 * The search and filters live in the URL, so a reload or Back returns to the same view.
 */
export const Route = createFileRoute('/_authenticated/exhibition/service-items')({
  // ponytail: the service orders skeleton has the same shape (a header and a table).
  pendingComponent: ServiceOrdersSkeleton,
  validateSearch: (search: Record<string, unknown>): ServiceFilters => ({
    q: text(search['q']),
    category: text(search['category'], 64),
    status: text(search['status'], 20),
  }),
  staticData: { crumb: { parents: [{ label: 'Exhibition' }], label: 'Service items' } },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(catalogQuery()),
      context.queryClient.ensureQueryData(serviceCategoriesQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ]),
  component: ServiceCatalogPage,
})

function ServiceCatalogPage() {
  const filters = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: catalog } = useSuspenseQuery(catalogQuery())
  const { data: categories } = useSuspenseQuery(serviceCategoriesQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'exhibitor.manage')

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Service items"
        description="The services that exhibitors order for their stands. Set the categories and their installation fees in the service terms."
      />

      {canManage ? null : <ReadOnlyNotice area="the service items" />}

      <ServiceCatalogTable
        items={catalog.items}
        categories={categories.items}
        currency={manifest?.tenant.currencyCode ?? 'IDR'}
        canManage={canManage}
        filters={filters}
        onFiltersChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })
        }
      />
    </div>
  )
}
